import DigiobcanApi from '../../api/DigiobcanApi';
import type { NewsPreference } from '../../features/queries/getUserNewsPreferences';
import { meGetNewsCategories } from '../../features/queries/meGetNewsCategories';
import { useM } from '../../lib/query';
import { useLogger } from '../../providers/LoggerProvider';
import { __, toastError } from '../../utils';
import {
  Box,
  Button,
  Stack,
  SwitchField,
  Typography,
} from '@presov.sk/presov-ui-react';
import { useQueryClient } from '@tanstack/react-query';
import React from 'react';

type Props = {
  preferences: NewsPreference[];
};

type Update = { category_id: string; subscribed: boolean };

/** Mriežka kariet - rovnaká pre obsah aj pre skeleton pri načítaní. */
export const PREFERENCES_GRID_SX = {
  display: 'grid',
  gap: 1,
  gridTemplateColumns: {
    xs: '1fr',
    md: 'repeat(2, minmax(0, 1fr))',
    xl: 'repeat(3, minmax(0, 1fr))',
  },
} as const;

/**
 * Odber kategórií noviniek. Kategórií je od migrácie 000025 sedem a ploché,
 * takže namiesto stromu s hľadaním je to mriežka kariet `SwitchField`:
 * názov, pod ním popis (úradník ho píše práve na to, aby občan vedel, čo
 * v kategórii nájde) a prepínač. Zmena sa ukladá hneď, bez tlačidla.
 */
const NewsPreferencesForm: React.FC<Props> = ({ preferences }) => {
  const { logger } = useLogger();
  const queryClient = useQueryClient();

  // Stav drží formulár, nie cache: prepínač sa prepne hneď a pri chybe sa
  // vráti. Zo servera sa berie len raz, pri otvorení sekcie.
  const [subscribed, setSubscribed] = React.useState(
    () => new Set(preferences.filter((p) => p.subscribed).map((p) => p.id)),
  );

  const apply = (updates: Update[]) =>
    setSubscribed((prev) => {
      const next = new Set(prev);
      updates.forEach(({ category_id, subscribed }) =>
        subscribed ? next.add(category_id) : next.delete(category_id),
      );
      return next;
    });

  const updateMutation = useM(
    (updates: Update[]) => DigiobcanApi.meUpdateNewsCategories({}, updates),
    {
      onMutate: (updates) => apply(updates),
      onSuccess: () =>
        // Domov radí novinky podľa odberov - musí sa o zmene dozvedieť.
        queryClient.invalidateQueries({
          queryKey: [meGetNewsCategories.toString()],
        }),
      onError: (error, updates) => {
        apply(updates.map((u) => ({ ...u, subscribed: !u.subscribed })));
        logger.error('Error updating user news categories', {
          action: 'updateUserNewsCategory',
          error,
          data: updates,
        });
        toastError(__('Nastala chyba pri aktualizácii kategórie noviniek.'));
      },
    },
  );

  const selectedCount = subscribed.size;

  return (
    <Stack gap={1.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        gap={1}
      >
        <Typography variant='body2' color='text.secondary' sx={{ flex: 1 }}>
          {__(
            'Vyberte si novinky, na ktoré chcete dostávať notifikácie a na domovskej stránke sa vám zobrazia medzi prvými.',
          )}
        </Typography>
        <Stack direction='row' alignItems='center' gap={1}>
          <Typography
            variant='body2'
            fontWeight={600}
            sx={{ whiteSpace: 'nowrap' }}
          >
            {selectedCount} {__('vybraných')}
          </Typography>
          <Button
            size='small'
            variant='text'
            disabled={!selectedCount || updateMutation.isPending}
            onClick={() =>
              updateMutation.mutate(
                [...subscribed].map((category_id) => ({
                  category_id,
                  subscribed: false,
                })),
              )
            }
          >
            {__('Zrušiť výber')}
          </Button>
        </Stack>
      </Stack>

      {preferences.length ? (
        <Box sx={PREFERENCES_GRID_SX}>
          {preferences.map((category) => (
            <SwitchField
              key={category.id}
              label={category.name}
              description={category.description}
              checked={subscribed.has(category.id)}
              onChange={(checked) =>
                updateMutation.mutate([
                  { category_id: category.id, subscribed: checked },
                ])
              }
            />
          ))}
        </Box>
      ) : (
        <Typography p={3} align='center' variant='body2'>
          {__('Zatiaľ tu nie sú žiadne kategórie noviniek.')}
        </Typography>
      )}
    </Stack>
  );
};

export default NewsPreferencesForm;
